<template>
  <div v-if="loaded" class="py-4">
    <button
      class="flex items-center gap-1 px-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
      @click="goBack"
    >
      <vue-feather type="chevron-left" class="!h-4 !w-4" />
      Back
    </button>
    <div
      v-if="isProcessing"
      class="w-11/12 mx-auto bg-blue-100 text-blue-900 p-4 lg:flex items-center justify-left gap-2 rounded-lg my-8 text-center lg:text-left"
    >
      <vue-feather type="alert-triangle" class="h-8 mx-auto lg:mx-0" />
      <div class="lg:ml-8">
        <header class="font-bold">Interview is Processing</header>
        <p class="mt-1">Interview recording, transcript, summary, and coaching will be available soon.</p>
      </div>
    </div>

    <div class="w-full">
      <div class="my-6 w-11/12 mx-auto">
        <MeetingDashboardHeader :contact-event="contactEvent" :contact="contact">
          <template #attendees>
            <EventRecordingAttendees :event-recording="contactEvent.event_recording" />
          </template>
        </MeetingDashboardHeader>
      </div>

      <div class="flex flex-col lg:flex-row items-start gap-8 w-11/12 mx-auto">
        <div class="lg:w-1/2 pr-12">
          <Interview
            v-if="!videoIsReady"
            :contact-event-location="contactEventLocation"
            :is-humaniz-conferencing="isHumanizConferencing"
            :event="contactEvent"
          />
          <video
            v-else
            :src="contactEvent.event_recording.daily_recording_url"
            controls
            class="h-[440px] border rounded-lg border-gray-300 bg-gray-200"
          />
        </div>
        <div class="lg:lg:w-1/2">
          <Tabs default-value="script">
            <TabsList>
              <TabsTrigger value="script">Script</TabsTrigger>
              <TabsTrigger v-if="isReady" value="transcript">Transcript</TabsTrigger>
              <TabsTrigger v-if="isReady" value="summary">Summary</TabsTrigger>
            </TabsList>
            <TabsContent value="script">
              <div class="h-[440px] flex flex-col overflow-scroll">
                <div class="flex-grow overflow-y-scroll" v-html="displayedScript"></div>
              </div>
            </TabsContent>
            <TabsContent value="transcript">
              <div class="h-[440px] flex flex-col overflow-scroll pt-4">
                <TranscriptViewer :transcript-string="contactEvent.event_recording.daily_transcription" />
              </div>
            </TabsContent>
            <TabsContent value="summary">
              <div class="h-[440px] flex flex-col overflow-scroll pt-4">
                {{ contactEvent.event_recording.daily_summary }}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <div class="border-t border-gray-200 mt-12">
        <div class="w-11/12 mx-auto flex flex-col lg:flex-row">
          <div class="lg:w-1/2 pr-12 border-r border-gray-200 py-12">
            <Tabs default-value="candidate">
              <TabsList>
                <TabsTrigger v-if="isReady" value="recruiter">Recruiter Scorecard</TabsTrigger>
                <TabsTrigger value="candidate">Candidate Scorecard</TabsTrigger>
              </TabsList>
              <TabsContent value="recruiter">
                <recruiter-scorecard :score="contactEvent.event_recording.score" />
              </TabsContent>
              <TabsContent value="candidate">
                <scorecard @update-contact="reload()" />
              </TabsContent>
            </Tabs>
          </div>
          <div class="lg:w-1/2 pl-12 py-12">
            <h3 class="text-lg leading-6 font-bold text-gray-900">Candidate Details</h3>
            <div class="px-4 py-5 sm:px-6">
              <div class="flex items-center justify-between">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  {{ contact.name }}
                </h3>
                <ContactStageBadge :stage="contact.stage" />
              </div>
              <div class="mt-4">
                <dt class="text-sm leading-5 font-medium text-gray-500">Email</dt>
                <dd class="mt-1 text-sm leading-5 text-gray-900">
                  <a class="text-indigo-600 hover:text-indigo-900" :href="`mailto:${contact.email}`">{{
                    contact.email
                  }}</a>
                </dd>
              </div>
              <div class="mt-4">
                <dt class="text-sm leading-5 font-medium text-gray-500">Phone</dt>
                <dd class="mt-1 text-sm leading-5 text-gray-900">
                  <a class="text-indigo-600 hover:text-indigo-900" :href="`tel:${contact.phone_number}`">{{
                    contact.phone_number
                  }}</a>
                </dd>
              </div>
              <router-link
                :to="`/contacts/${contact.id}`"
                class="mt-12 flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >View candidate</router-link
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Scorecard from './Scorecard.vue';
import ContactStageBadge from '../contact/ContactStageBadge.vue';
import Interview from './Interview.vue';
import RecruiterScorecard from './RecruiterScorecard.vue';
import TranscriptViewer from './TranscriptViewer.vue';
import EventRecordingAttendees from '../EventRecordingAttendees/index.vue';
import MeetingDashboardHeader from './MeetingDashboardHeader.vue';
import { useRouter } from 'vue-router';

export default {
  components: {
    Scorecard,
    ContactStageBadge,
    Interview,
    RecruiterScorecard,
    TranscriptViewer,
    EventRecordingAttendees,
    MeetingDashboardHeader,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
  },
  setup() {
    const router = useRouter();
    const goBack = () => {
      if (window.history.state.back) {
        router.back();
      } else {
        router.push({ name: 'Dashboard' });
      }
    };
    return { router, goBack };
  },
  data() {
    return {
      loaded: false,
      contactEvent: null,
      contact: null,
      scripts: {
        team: null,
        global: null,
      },
      showDaily: false,
    };
  },
  computed: {
    displayedScript() {
      if (!this.scripts.team && !this.scripts.global) return;

      if (this.scripts.team.length === 0) {
        return this.scripts.global.find(s => s.interview_type === this.contactEvent.event_type)?.content;
      } else {
        const teamScriptSettings = this.scripts.team.find(s => s.interview_type === this.contactEvent.event_type);

        if (teamScriptSettings?.interview_method === 'humaniz') {
          return this.scripts.global.find(s => s.id === teamScriptSettings.global_script_id)?.content;
        } else {
          return null;
        }

        return teamScriptSettings?.custom_script_content;
      }
    },
    contactEventLocation() {
      return this.contactEvent.location;
    },
    isHumanizConferencing() {
      if (this.contactEventLocation) {
        return this.contactEventLocation.includes('humaniz') || this.contactEventLocation.includes('daily.co');
      } else {
        return false;
      }
    },
    eventIsOver() {
      const now = new Date();
      return new Date(this.contactEvent.start_time) <= now && new Date(this.contactEvent.end_time) <= now;
    },
    isProcessing() {
      return this.eventIsOver && this.isHumanizConferencing && !this.isReady;
    },
    isReady() {
      if (!this.contactEvent.event_recording) return false;

      const recordingUrl = this.contactEvent?.event_recording?.daily_recording_url;
      const score = this.contactEvent.event_recording.score;

      return recordingUrl && !isEmpty(score);
    },
    videoIsReady() {
      if (!this.contactEvent.event_recording) return false;
      if (!this.contactEvent.event_recording.daily_recording_url) return false;
      if (new Date(this.contactEvent.end_time) > new Date()) return false;

      return true;
    },
  },
  async mounted() {
    document.body.classList.remove('lg:overflow-hidden');
    await this.reload();
    await this.fetchScripts();
    this.loaded = true;

    if (this.isReady) {
      this.selectedTab = 'Recruiter Scorecard';
    }
  },
  methods: {
    handleSelectedTab(tab) {
      this.selectedTab = tab;
    },
    async reload() {
      const res = await fetch(`/api/contact_events/${this.$route.params.id}`);
      const { contact, ...contactEvent } = await res.json();

      this.contactEvent = contactEvent;
      this.contact = contact;
    },
    async fetchScripts() {
      const res = await fetch(`/api/interview_scripts`);
      const { records } = await res.json();

      this.scripts.team = records.team;
      this.scripts.global = records.global;
    },
  },
};
</script>
